import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  CartesianGrid,
  Legend,
} from "recharts";

/**
 * Covered-call P&L at expiration (per share, then ×100 for contract value).
 * Uses live option mid prices — no need for the user to remember the
 * original premium they collected.
 *
 *   pnlCurrent = min(S, K_current) - cost + currentOptMid
 *   pnlRolled  = min(S, K_new)     - cost + currentOptMid + netCredit
 *
 * where cost = current underlying price (approximation).
 */
export const PayoffChart = ({ position, underlying, currentOption, newOption, netCredit }) => {
  const data = useMemo(() => {
    if (!position || !underlying || !currentOption) return [];
    const minPx = Math.max(1, position.strike * 0.7);
    const maxPx = position.strike * 1.3;
    const step = (maxPx - minPx) / 60;
    const cost = underlying;
    const currentMid = currentOption.mid || 0;
    const arr = [];
    for (let S = minPx; S <= maxPx + 0.001; S += step) {
      const pnlCurrent = Math.min(S, position.strike) - cost + currentMid;
      let pnlNew = null;
      if (newOption && netCredit !== undefined && netCredit !== null) {
        pnlNew = Math.min(S, newOption.strike) - cost + currentMid + netCredit;
      }
      arr.push({
        price: Number(S.toFixed(2)),
        current: Number((pnlCurrent * 100).toFixed(2)),
        rolled: pnlNew !== null ? Number((pnlNew * 100).toFixed(2)) : null,
      });
    }
    return arr;
  }, [position, underlying, currentOption, newOption, netCredit]);

  if (!position) {
    return (
      <div className="panel" data-testid="payoff-empty">
        <div className="panel-h">
          <div className="label-cap">P&L · Payoff at Expiration</div>
        </div>
        <div className="p-6 text-center text-xs text-[#71717A]">
          Select a position to visualize the P&L profile.
        </div>
      </div>
    );
  }

  return (
    <div className="panel" data-testid="payoff-panel">
      <div className="panel-h">
        <div>
          <div className="label-cap">P&L · Payoff at Expiration</div>
          <div className="text-[11px] font-mono text-[#71717A] mt-1">
            Per contract (×100 shares) · cost basis = current underlying ${underlying?.toFixed(2)}
          </div>
        </div>
      </div>
      <div className="p-2 h-[280px] min-h-[260px] min-w-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 16, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid stroke="#1A1A1A" strokeDasharray="2 4" />
            <XAxis
              dataKey="price"
              stroke="#71717A"
              tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
              tickFormatter={(v) => `$${v.toFixed(0)}`}
            />
            <YAxis
              stroke="#71717A"
              tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
              tickFormatter={(v) => `$${v}`}
            />
            <Tooltip
              contentStyle={{
                background: "#0A0A0A",
                border: "1px solid #27272A",
                fontSize: 12,
                fontFamily: "JetBrains Mono",
              }}
              labelFormatter={(v) => `Underlying $${v}`}
              formatter={(v, name) => [v !== null ? `$${v.toFixed(2)}` : "—", name === "current" ? "Current" : "After Roll"]}
            />
            <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Sans" }} />
            <ReferenceLine x={underlying} stroke="#007AFF" strokeDasharray="3 3" label={{ value: "Spot", fill: "#007AFF", fontSize: 10, position: "top" }} />
            <ReferenceLine y={0} stroke="#3F3F46" />
            <Line
              type="monotone"
              dataKey="current"
              name="Current"
              stroke="#A1A1AA"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            {newOption && (
              <Line
                type="monotone"
                dataKey="rolled"
                name="After Roll"
                stroke="#00E676"
                strokeWidth={2.5}
                dot={false}
                isAnimationActive={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
